import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, UserPlus, Search } from 'lucide-react';

const navItems = [
  { path: '/', label: '首页' },
  { path: '/about', label: '关于我们' },
  { path: '/products', label: '产品中心' },
  { path: '/news', label: '新闻资讯' },
  { path: '/contact', label: '联系我们' }
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showJoinQR, setShowJoinQR] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    // 检查当前滚动位置来设置状态
    setIsScrolled(window.scrollY > 50);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex flex-col">
              <img
                src="/images/logo.png?v=1"
                alt="民间艺人"
                className={`h-10 w-auto transition-all duration-300 ${
                  isScrolled ? 'brightness-75' : 'brightness-100'
                }`}
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const baseClasses = 'relative text-sm font-medium transition-all duration-300 group';
              const colorClasses = isScrolled
                ? isActive ? 'text-dawn-orange' : 'text-gray-800 hover:text-deep-lake'
                : isActive ? 'text-dawn-orange' : 'text-[#1F2937] hover:text-dawn-orange';
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${baseClasses} ${colorClasses}`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-dawn-orange transition-all duration-300 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {/* 搜索按钮 */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={`p-2.5 rounded-full transition-all duration-300 ${
                isScrolled
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-[#1F2937] hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              <Search className="w-5 h-5" />
            </button>
            <div className="relative">
              <button
                onMouseEnter={() => setShowJoinQR(true)}
                onMouseLeave={() => setShowJoinQR(false)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  isScrolled
                    ? 'bg-deep-lake text-white hover:bg-deep-lake-light'
                    : 'bg-[#1F2937] text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                <UserPlus className="w-4 h-4" />
                加入我们
              </button>
              {showJoinQR && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 p-3 bg-white rounded-xl shadow-2xl animate-fade-in z-50">
                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-xs text-gray-400 text-center"><img src="/images/ali-qr.png?v=1" /></span>
                  </div>
                  <p className="text-xs text-gray-600 text-center mt-2">扫码咨询合作</p>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45" />
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${
              isScrolled ? 'text-deep-lake' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* 搜索框展开区域 */}
        {isSearchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg animate-fade-in">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchQuery.trim()) {
                        navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
                        setIsSearchOpen(false);
                        setSearchQuery('');
                      }
                    }}
                    placeholder="搜索产品..."
                    className="w-full px-4 py-3 pl-12 bg-gray-100 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-deep-lake/50"
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <button
                  onClick={() => {
                    if (searchQuery.trim()) {
                      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
                      setIsSearchOpen(false);
                      setSearchQuery('');
                    }
                  }}
                  className="px-6 py-3 bg-deep-lake text-white rounded-lg hover:bg-deep-lake-light transition-colors"
                >
                  搜索
                </button>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-3 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200/20 animate-fade-in">
            <nav className="flex flex-col gap-2 pt-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === item.path
                      ? 'bg-deep-lake text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
